import qs from "query-string";
import isEqual from "lodash/isEqual";
import type {
  AuthorDetailedSerializer,
  IApiResponse,
  ArtShortSerializer,
  ArtGroupsEnum,
  ArtWithTopQuoteSerializer,
  AuthorShortSerializer,
  BasePaginationSerializer,
  PersonShortSerializer,
  PromoArtSerializer,
  PersonInterviewDetailSerializer,
  ReactionTypeEnum,
} from "@litres-libs/types";
import { ArtSortTypesEnum } from "@litres-libs/types";
import type {
  IPopularAuthorRequest,
  IUsersAuthorsRequest,
  PersonSubscribeSource,
} from "./person.types";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

const paramsSerializer = (data: Record<string, any>) =>
  qs.stringify(data, { arrayFormat: "none" });

export const personApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getAuthor: build.query<AuthorDetailedSerializer, number | string>({
      query: authorIdOrSlug => ({
        url: `/foundation/api/authors/${authorIdOrSlug}`,
      }),
      transformResponse: (response: IApiResponse<AuthorDetailedSerializer>) => {
        return response.payload.data;
      },
    }),

    getPersonArts: build.query<
      { data: ArtShortSerializer[]; pagination: BasePaginationSerializer },
      {
        authorIdOrSlug?: number | string;
        art_groups?: ArtGroupsEnum[];
        limit?: number;
        show_unavailable?: boolean;
        is_recommendation?: boolean;
        sort?: ArtSortTypesEnum;
        language?: string;
        requestUrl?: string | null;
      }
    >({
      query: ({
        authorIdOrSlug,
        limit,
        art_groups,
        show_unavailable,
        is_recommendation,
        sort,
        language,
        requestUrl,
      }) => ({
        url: requestUrl
          ? `/foundation${requestUrl}`
          : `/foundation/api/authors/${authorIdOrSlug}/arts${
              is_recommendation ? "/recommended" : ""
            }?${paramsSerializer({ art_groups })}`,
        params: requestUrl
          ? undefined
          : {
              limit: limit || 24,
              show_unavailable: show_unavailable || false,
              o: sort || ArtSortTypesEnum.popular,
              language: language,
            },
      }),
      transformResponse: (response: IApiResponse<ArtShortSerializer[]>) => {
        return response.payload;
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),
    getAuthorsQuotesWithArts: build.query<
      {
        data: ArtWithTopQuoteSerializer[];
        pagination: BasePaginationSerializer;
        total_count?: number;
      },
      {
        authorIdOrSlug: number | string;
        offset: number;
        limit: number;
        needMerge: boolean;
      }
    >({
      query: ({ authorIdOrSlug, offset, limit }) => ({
        url: `/foundation/api/authors/${authorIdOrSlug}/quotes`,
        params: { offset, limit },
      }),
      transformResponse: (
        response: IApiResponse<ArtWithTopQuoteSerializer[]>
      ) => {
        return response.payload;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.offset > 0 && arg.needMerge) {
          currentCache.data.push(...newResponse.data);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
        currentCache.total_count = newResponse.total_count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),

    getAuthorsRecommend: build.query<
      { data: ArtShortSerializer[]; pagination: BasePaginationSerializer },
      { authorIdOrSlug: number | string; offset: number; limit: number }
    >({
      query: ({ authorIdOrSlug, offset, limit }) => ({
        url: `/foundation/api/authors/${authorIdOrSlug}/arts/recommended`,
        params: { offset, limit },
      }),
      transformResponse: (response: IApiResponse<ArtShortSerializer[]>) => {
        return response.payload;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.offset > 0) {
          currentCache.data.push(...newResponse.data);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
            ]
          : [TagsType.ART_SHORT_LIST],
    }),

    getSimilarAuthors: build.query<
      AuthorShortSerializer[],
      { authorIdOrSlug: number | string; limit?: number }
    >({
      query: ({ authorIdOrSlug, limit }) => ({
        url: `/foundation/api/authors/${authorIdOrSlug}/similar`,
        params: { limit: limit || 5 },
      }),
      transformResponse: (response: IApiResponse<AuthorShortSerializer[]>) => {
        return response.payload.data;
      },
    }),

    getPopularAuthors: build.query<
      IApiResponse<PersonShortSerializer[]>["payload"],
      IPopularAuthorRequest
    >({
      query: params => ({
        url: "/foundation/api/authors/popular",
        params: params,
      }),
      transformResponse: (response: IApiResponse<PersonShortSerializer[]>) =>
        response.payload,
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          currentCache.data.push(...newResponse.data);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),

    getPromoArts: build.query<
      PromoArtSerializer[],
      { authorIdOrSlug: number | string }
    >({
      query: ({ authorIdOrSlug }) => ({
        url: `/foundation/api/authors/${authorIdOrSlug}/promo-art`,
      }),
      transformResponse: (response: IApiResponse<PromoArtSerializer[]>) => {
        return response.payload.data;
      },
    }),

    subscribeAction: build.mutation<
      any,
      {
        authorId: number | string;
        shouldSubscribe: boolean;
        source?: PersonSubscribeSource;
      }
    >({
      query: ({ authorId, shouldSubscribe }) => ({
        url: `/foundation/api/persons/${authorId}/subscribe`,
        method: shouldSubscribe ? "PUT" : "DELETE",
      }),
      async onQueryStarted({ authorId, source }, { dispatch, queryFulfilled }) {
        let patchRemoveAuthor;
        switch (source) {
          case "my authors page":
            {
              patchRemoveAuthor = dispatch(
                personApi.util.updateQueryData("getUsersAuthors", {}, draft => {
                  draft.data = draft.data.filter(
                    cachedAuthor => cachedAuthor.id !== authorId
                  );
                })
              );
            }
            break;
        }
        try {
          await queryFulfilled;
        } catch (e) {
          console.log(e);
          patchRemoveAuthor?.undo();
        }
      },
      invalidatesTags: [{ type: TagsType.MY_BOOKS, id: "STATS" }],
    }),
    getUsersAuthors: build.query<
      IApiResponse<PersonShortSerializer[]>["payload"],
      IUsersAuthorsRequest
    >({
      query: params => ({
        url: `/foundation/api/users/me/authors`,
        params: params,
      }),
      transformResponse: (response: IApiResponse<PersonShortSerializer[]>) => {
        return response.payload;
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          currentCache.data.push(...newResponse.data);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
    }),
    getAuthorInterview: build.query<
      PersonInterviewDetailSerializer,
      { personIdOrSlug: string | number; interviewId: number }
    >({
      query: params => ({
        url: `/foundation/api/persons/${params.personIdOrSlug}/interviews/${params.interviewId}`,
      }),
      transformResponse: (
        response: IApiResponse<PersonInterviewDetailSerializer>
      ) => {
        return response.payload.data;
      },
    }),

    addLikeToInterview: build.mutation<
      void,
      { personIdOrSlug: string | number; interviewId: number }
    >({
      query: params => {
        return {
          url: `/foundation/api/persons/interviews/${params.interviewId}/likes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedInterview = dispatch(
          personApi.util.updateQueryData(
            "getAuthorInterview",
            params,
            draftInterview => {
              const isArticleDislikedByUser =
                draftInterview.user_reaction === "dislike";
              Object.assign<
                PersonInterviewDetailSerializer,
                Partial<PersonInterviewDetailSerializer>
              >(draftInterview, {
                user_reaction: "like" as ReactionTypeEnum,
                likes_count: draftInterview.likes_count + 1,
                ...(isArticleDislikedByUser && {
                  dislikes_count: draftInterview.dislikes_count - 1,
                }),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedInterview.undo();
        }
      },
    }),

    removeLikeFromInterview: build.mutation<
      void,
      { personIdOrSlug: string | number; interviewId: number }
    >({
      query: params => {
        return {
          url: `/foundation/api/persons/interviews/${params.interviewId}/likes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedInterview = dispatch(
          personApi.util.updateQueryData(
            "getAuthorInterview",
            params,
            draftInterview => {
              Object.assign<
                PersonInterviewDetailSerializer,
                Partial<PersonInterviewDetailSerializer>
              >(draftInterview, {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: null,
                likes_count: draftInterview.likes_count - 1,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedInterview.undo();
        }
      },
    }),

    addDislikeToInterview: build.mutation<
      string,
      { personIdOrSlug: string | number; interviewId: number }
    >({
      query: params => {
        return {
          url: `/foundation/api/persons/interviews/${params.interviewId}/dislikes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedInterview = dispatch(
          personApi.util.updateQueryData(
            "getAuthorInterview",
            params,
            draftInterview => {
              const isArticleLikedByUser =
                draftInterview.user_reaction === "like";
              Object.assign<
                PersonInterviewDetailSerializer,
                Partial<PersonInterviewDetailSerializer>
              >(draftInterview, {
                user_reaction: "dislike" as ReactionTypeEnum,
                dislikes_count: draftInterview.dislikes_count + 1,
                ...(isArticleLikedByUser && {
                  likes_count: draftInterview.likes_count - 1,
                }),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedInterview.undo();
        }
      },
    }),

    removeDislikeFromInterview: build.mutation<
      string,
      { personIdOrSlug: string | number; interviewId: number }
    >({
      query: params => {
        return {
          url: `/foundation/api/persons/interviews/${params.interviewId}/dislikes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedInterview = dispatch(
          personApi.util.updateQueryData(
            "getAuthorInterview",
            params,
            draftInterview => {
              Object.assign<
                PersonInterviewDetailSerializer,
                Partial<PersonInterviewDetailSerializer>
              >(draftInterview, {
                user_reaction: null,
                dislikes_count: draftInterview.dislikes_count - 1,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedInterview.undo();
        }
      },
    }),
  }),
});
